import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { NOTE_SHELL, STATUS_SHELL, STATUS_TONE, type StatusTone } from './statusTone';

export interface AlertProps {
  tone?: StatusTone;
  /**
   * `message` (the default) reports something that just happened or went
   * wrong, and is announced. `note` is a standing fact about the page — flat,
   * tinted, and not announced, so it never reads as a toast.
   */
  variant?: 'message' | 'note';
  title?: string;
  children: ReactNode;
  className?: string;
}

/**
 * A message that stays on the page.
 *
 * For anything the reader has to act on or keep looking at — a field that
 * failed validation, a list that would not load, a permission they do not have.
 * The outcome of an action they just took is a `toast` instead; the rule is
 * written out in `hooks/useToast.ts`, and both are drawn from the same tone
 * table so they cannot drift apart.
 */
export function Alert({
  tone = 'info',
  variant = 'message',
  title,
  children,
  className,
}: AlertProps) {
  const { icon, tile, bar, note } = STATUS_TONE[tone];
  const isNote = variant === 'note';

  return (
    <div
      // role="alert" makes screen readers announce it the moment it appears —
      // important for the "wrong password" message after a failed submit. A
      // note is part of the page, read in its place like any other text.
      role={isNote ? 'note' : 'alert'}
      className={cn(isNote ? [NOTE_SHELL, note] : [STATUS_SHELL, 'animate-fade-up'], className)}
    >
      <span className={cn('absolute inset-y-0 left-0 w-1', bar)} aria-hidden />

      <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-xl', tile)} aria-hidden>
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        {title && <p className="text-sm font-semibold text-ink">{title}</p>}
        {/* A div, not a p: several callers pass a list of what is blocking a
            save, and a `ul` inside a `p` is invalid markup the browser silently
            reparents. */}
        <div
          className={cn(
            'text-sm leading-relaxed',
            tone === 'critical' ? 'text-red-600 dark:text-red-400 font-medium' : 'text-ink-2',
            title && 'mt-0.5',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
